import { useNotification } from "providers";
import { ApiResponse, ICourse, ICourseStat, IInterview, IModule, IQuestion, IQuestionAnswer, IQuiz, IQuizYear, IUserCourse, IUserInterview, IUserQuestionAnswer, IUserQuiz } from "utils/types";
import { useCourseService } from "./services";
import { 
    ICreateCourseRequest,
    ICreateQuizRequest,
    ICreateUserCourseRequest,
    IDeleteCourseRequest, IDeleteQuestionsByQuizIdRequest, IDeleteQuizAndGetQuizYearsRequest, 
    IGetCourseModuleRequest, 
    IGetCourseModulesRequest, 
    IGetCourseRequest, IGetCoursesRequest, IGetQuizRequest, IGetQuizYearsRequest, 
    IGetQuizzesRequest, IGetRecommendedCoursesRequest, 
    IGetUserCourseRequest, IGetUserCoursesRequest, IGetUserQuizzesRequest, 
    IUpdateCourseRequest, 
    IUpdateCourseModulesRequest, 
    IUpdateLessonsRequest, 
    IUpdateUserCourseRequest,
    IAddLessonsRequest,
    IAddCourseModulesRequest,
    IGetQuestionsRequest,
    IScoreQuizRequest,
    IGetQuestionAnswersRequest,
    IGetUserQuestionAnswersRequest,
    IGetCourseModuleCountRequest,
    IDeleteQuestionOptionRequest,
    IUpdateQuizUnifiedRequest,
    ICreateInterviewRequest,
    IUpdateInterviewRequest,
    IDeleteInterviewRequest,
    IGetInterviewRequest,
    IGetInterviewsRequest,
    ICreateUserInterviewRequest,
    IGetUserInterviewsRequest,
    IGetUserInterviewRequest,
    IDeleteUserInterviewRequest,
    IUpdateUserInterviewRequest,
    IUpdateInterviewUnifiedRequest,
    IScoreInterviewRequest,
    IDeleteCourseModuleRequest,
    IDeleteCourseModuleResponse,
} from "./types";
import { handleRequest } from "utils";

export const useCourse = () => {
    const notification = useNotification()
    const courseService = useCourseService()
    const request:typeof handleRequest = (requestFn, input) => {
        return handleRequest(requestFn, input, notification)
    };
    return {
        scoreInterview: (input: IScoreInterviewRequest) => request(async (input: IScoreInterviewRequest): Promise<ApiResponse<IUserInterview>> => {
            try {
                const { data, errors } = await courseService.scoreInterview({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.scoreInterview.userInterview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getUserInterviews: (input: IGetUserInterviewsRequest) => request(async (input: IGetUserInterviewsRequest): Promise<ApiResponse<IUserInterview[]>> => {
            try {
                const { data, error } = await courseService.getUserInterviews({
                    variables: input,
                    fetchPolicy: "network-only",
                })
                if(error) throw error
                return {
                    success: true,
                    data: data?.getUserInterviews.userInterviews
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getUserInterview: (input: IGetUserInterviewRequest) => request(async (input: IGetUserInterviewRequest): Promise<ApiResponse<IUserInterview>> => {
            try {
                const { data, error } = await courseService.getUserInterview({
                    variables: input,
                    fetchPolicy: "network-only",
                })
                if(error) throw error
                return {
                    success: true,
                    data: data?.getUserInterview.userInterview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteUserInterview: (input: IDeleteUserInterviewRequest) => request(async (input: IDeleteUserInterviewRequest): Promise<ApiResponse<string>> => {
            try {
                const { data, errors } = await courseService.deleteUserInterview({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteUserInterview.userInterviewId
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateUserInterview: (input: IUpdateUserInterviewRequest) => request(async (input: IUpdateUserInterviewRequest): Promise<ApiResponse<IUserInterview>> => {
            try {
                const { data, errors } = await courseService.updateUserInterview({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateUserInterview.userInterview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        createUserInterview: (input: ICreateUserInterviewRequest) => request(async (input: ICreateUserInterviewRequest): Promise<ApiResponse<IUserInterview>> => {
            try {
                const { data, errors } = await courseService.createUserInterview({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.createUserInterview.userInterview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),


        // interviews 
         updateInterviewUnified: (input: IUpdateInterviewUnifiedRequest) => request( async (input: IUpdateInterviewUnifiedRequest): Promise<ApiResponse<IInterview>> => {
            try {
                const { data, errors } = await courseService.updateInterviewUnified({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateInterviewUnified.interview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getInterviews: (input: IGetInterviewsRequest) => request(async (input: IGetInterviewsRequest): Promise<ApiResponse<IInterview[]>> => {
            try {
                const { data, error } = await courseService.getInterviews({
                    variables: input,
                    fetchPolicy: "network-only",
                })
                if(error) throw error
                return {
                    success: true,
                    data: data?.getInterviews.interviews
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getInterview: (input: IGetInterviewRequest) => request(async (input: IGetInterviewRequest): Promise<ApiResponse<IInterview>> => {
            try {
                const { data, error } = await courseService.getInterview({
                    variables: input,
                    fetchPolicy: "network-only",
                })
                if(error) throw error
                return {
                    success: true,
                    data: data?.getInterview.interview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteInterview: (input: IDeleteInterviewRequest) => request(async (input: IDeleteInterviewRequest): Promise<ApiResponse<string>> => {
            try {
                const { data, errors } = await courseService.deleteInterview({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteInterview.interviewId
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateInterview: (input: IUpdateInterviewRequest) => request(async (input: IUpdateInterviewRequest): Promise<ApiResponse<IInterview>> => {
            try {
                const { data, errors } = await courseService.updateInterview({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateInterview.interview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        createInterview: (input: ICreateInterviewRequest) => request(async (input: ICreateInterviewRequest): Promise<ApiResponse<IInterview>> => {
            try {
                const { data, errors } = await courseService.createInterview({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.createInterview.interview
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),

        // courses 
        createCourse: (input: ICreateCourseRequest) => request(async (input: ICreateCourseRequest): Promise<ApiResponse<ICourse>> => {
            try {
                const { data, errors } = await courseService.createCourse({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.createCourse.course
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateCourse: (input: IUpdateCourseRequest) => request(async (input: IUpdateCourseRequest): Promise<ApiResponse<ICourse>> => {
            try {
                const { data, errors } = await courseService.updateCourse({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateCourse.course
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getQuizYears: (input: IGetQuizYearsRequest) => request(async (input: IGetQuizYearsRequest): Promise<ApiResponse<IQuizYear[]>> => {
            try {
                const { data, error } = await courseService.getQuizYears({
                    variables: input,
                    fetchPolicy: "network-only",
                })
                if(error) throw error
                return {
                    success: true,
                    data: data?.getQuizYears.quizYears
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteQuizAndGetQuizYears: (input: IDeleteQuizAndGetQuizYearsRequest) => request(async (input: IDeleteQuizAndGetQuizYearsRequest): Promise<ApiResponse<IQuizYear[]>> => {
            try {
                const { data, errors } = await courseService.deleteQuizAndGetQuizYears({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteQuizAndGetQuizYears.quizYears
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateUserCourse: (input: IUpdateUserCourseRequest) => request(async (input: IUpdateUserCourseRequest): Promise<ApiResponse<IUserCourse>> => {
            try {
                const { data, errors } = await courseService.updateUserCourse({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateUserCourse.userCourse
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getQuizzes: (input: IGetQuizzesRequest) => request(async (input: IGetQuizzesRequest): Promise<ApiResponse<IQuiz[]>> => {
            try {
                const { data, error } = await courseService.getQuizzes({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getQuizzes.quizzes,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        createQuiz: (input: ICreateQuizRequest) => request( async (input: ICreateQuizRequest): Promise<ApiResponse<IQuiz>> => {
            try {
                const { data, errors } = await courseService.createQuiz({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.createQuiz.quiz
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateQuizUnified: (input: IUpdateQuizUnifiedRequest) => request( async (input: IUpdateQuizUnifiedRequest): Promise<ApiResponse<IQuiz>> => {
            try {
                const { data, errors } = await courseService.updateQuizUnified({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateQuizUnified.quiz
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getQuiz: (input: IGetQuizRequest) => request(async (input: IGetQuizRequest): Promise<ApiResponse<IQuiz>> => {
            try {
                const { data, error } = await courseService.getQuiz({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getQuiz.quiz,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        scoreQuiz: (input: IScoreQuizRequest) => request(async (input: IScoreQuizRequest): Promise<ApiResponse<IUserQuiz>> => {
            try {
                const { data, errors } = await courseService.scoreQuiz({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.scoreQuiz.userQuiz
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getUserQuizzes: (input: IGetUserQuizzesRequest) => request(async (input: IGetUserQuizzesRequest): Promise<ApiResponse<IUserQuiz[]>> => {
            try {
                const { data, error } = await courseService.getUserQuizzes({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getUserQuizzes.userQuizzes,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        geUserQuestionAnswers: (input: IGetUserQuestionAnswersRequest) => request(async (input: IGetUserQuestionAnswersRequest): Promise<ApiResponse<IUserQuestionAnswer[]>> => {
            try {
                const { data, error } = await courseService.getUserQuestionAnswers({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getUserQuestionAnswers.userQuestionAnswers,
                    success: true,  
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        addLessons: (input: IAddLessonsRequest) => request(async (input: IAddLessonsRequest): Promise<ApiResponse<string[]>> => {
            try {
                const { data, errors } = await courseService.addLessons({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.addLessons.lessonIds
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateLessons: (input: IUpdateLessonsRequest) => request(async (input: IUpdateLessonsRequest): Promise<ApiResponse<string[]>> => {
            try {
                const { data, errors } = await courseService.updateLessons({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateLessons.lessonIds
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),

        // Course modules 
        getCourseModuleCount: (input: IGetCourseModuleCountRequest) => request(async (input: IGetCourseModuleCountRequest): Promise<ApiResponse<number>> => {
            try {
                const { data, error } = await courseService.getCourseModuleCount({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getCourseModuleCount.moduleCount,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        addCourseModules: (input: IAddCourseModulesRequest) => request(async (input: IAddCourseModulesRequest): Promise<ApiResponse<string[]>> => {
            try {
                const { data, errors } = await courseService.addCourseModules({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.addCourseModules.courseModuleIds
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        updateCourseModules: (input: IUpdateCourseModulesRequest) => request(async (input: IUpdateCourseModulesRequest): Promise<ApiResponse<string[]>> => {
            try {
                const { data, errors } = await courseService.updateCourseModules({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.updateCourseModules.courseModuleIds
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getCourseModules: (input: IGetCourseModulesRequest) => request(async (input: IGetCourseModulesRequest): Promise<ApiResponse<IModule[]>> => {
            try {
                const { data, error } = await courseService.getCourseModules({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getCourseModules.courseModules,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getCourseModule: (input: IGetCourseModuleRequest) => request(async (input: IGetCourseModuleRequest): Promise<ApiResponse<IModule>> => {
            try {
                const { data, error } = await courseService.getCourseModule({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getCourseModule.courseModule,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteCourseModule: (input: IDeleteCourseModuleRequest) => request(async (input: IDeleteCourseModuleResponse): Promise<ApiResponse<string>> => {
            try {
                const { data, errors } = await courseService.deleteCourseModule({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteCourseModule.courseModuleId
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        createUserCourse: (input: ICreateUserCourseRequest) => request(async (input: ICreateUserCourseRequest): Promise<ApiResponse<IUserCourse>> => {
            try {
                const { data, errors } = await courseService.createUserCourse({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.createUserCourse.userCourse
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getUserCourse: (input: IGetUserCourseRequest) => request(async (input: IGetUserCourseRequest): Promise<ApiResponse<IUserCourse>> => {
            try {
                const { data, error } = await courseService.getUserCourse({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getUserCourse.userCourse,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getUserCourses: (input: IGetUserCoursesRequest) => request(async (input: IGetUserCoursesRequest): Promise<ApiResponse<IUserCourse[]>> => {
            try {
                const { data, error } = await courseService.getUserCourses({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getUserCourses.userCourses,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getRecommendedCourses: (input: IGetRecommendedCoursesRequest) => request(async (input: IGetRecommendedCoursesRequest): Promise<ApiResponse<ICourse[]>> => {
            try {
                const { data, error } = await courseService.getRecommendedCourses({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getRecommendedCourses.courses,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getCourse: (input: IGetCourseRequest) => request(async (input: IGetCourseRequest): Promise<ApiResponse<ICourse>> => {
            try {
                const { data, error } = await courseService.getCourse({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getCourse.course,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getCourses: (input: IGetCoursesRequest) => request( async (input: IGetCoursesRequest): Promise<ApiResponse<ICourse[]>> => {
            try {
                const { data, error } = await courseService.getCourses({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getCourses.courses,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteCourse: (input: IDeleteCourseRequest) => request(async (input: IDeleteCourseRequest): Promise<ApiResponse<string>> => {
            try {
                const { data, errors } = await courseService.deleteCourse({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteCourse.courseId
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getQuestionAnswers: (input: IGetQuestionAnswersRequest) => request(async (input: IGetQuestionAnswersRequest): Promise<ApiResponse<IQuestionAnswer[]>> => {
            try {
                const { data, error } = await courseService.getQuestionAnswers({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getQuestionAnswers.questionAnswers,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteQuestionOption: (input: IDeleteQuestionOptionRequest) => request(async (input: IDeleteQuestionOptionRequest): Promise<ApiResponse<string>> => {
            try {
                const { data, errors } = await courseService.deleteQuestionOption({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteQuestionOption.questionOptionId
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getQuestions: (input: IGetQuestionsRequest) => request(async (input: IGetQuestionsRequest): Promise<ApiResponse<IQuestion[]>> => {
            try {
                const { data, error } = await courseService.getQuestions({
                    variables: input,
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getQuestions.questions,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        deleteQuestionsByQuizId: (input: IDeleteQuestionsByQuizIdRequest) => request(async (input: IDeleteQuestionsByQuizIdRequest): Promise<ApiResponse<string>> => {
            try {
                const { data, errors } = await courseService.deleteQuestionsByQuizId({
                    variables: input
                })
                if(errors?.[0]) throw errors[0]
                return {
                    success: true,
                    data: data?.deleteQuestionsByQuizId.quizId
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, input),
        getCourseStat: () => request(async (): Promise<ApiResponse<ICourseStat>> => {
            try {
                const { data, error } = await courseService.getCourseStat({
                    fetchPolicy: "network-only"
                })
                if(error) throw error
                return {
                    data: data?.getCourseStat.courseStat,
                    success: true,
                }
            }catch(e){
                return {
                    err: (e as Error)
                }
            }
        }, null),
    };
};